// ###### IMPORTS
import { apiClient } from "connections/apiClient";
import apiRoutes from "connections/apiRoutes";

// ###### OPTION MAPPINGS (Frontend to Backend)
// Maps frontend agnostic option keys to actual backend values

const LOCATION_MAPPING = {
  location_option_1: "node1_location",
  location_option_2: "node2_location",
  location_option_3: "node3_location",
};

// ###### API CALLS
export async function StartCalculations(payload) {
  try {
    const response = await apiClient.post(apiRoutes.cockpit.start, payload);
    console.log("Start Calculations - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to start calculations:", error);
    throw (
      error.response.data.error_key ||
      error.response.data.message ||
      error.message
    );
  }
}

export async function getCockpitVariants() {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.variants);
    console.log("Get Cockpit Variants - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get cockpit variants:", error);
    throw error;
  }
}

export async function getCockpitSchemaInputs() {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.schemaInputs);
    console.log("Get Cockpit Schema Inputs - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get cockpit schema inputs:", error);
    throw error;
  }
}

export async function getScenarioStatus() {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.scenariosData);
    console.log("Get Scenario Status - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get scenario status:", error);
    throw error;
  }
}

export async function getOutputTableData(
  scenarioId,
  fullTable = false,
  download = false,
  filters = {},
) {
  try {
    const params = {
      scenario_id: scenarioId,
      full_table: fullTable,
      download: download,
    };

    if (filters.products && filters.products.length > 0) {
      params.product = Array.isArray(filters.products)
        ? JSON.stringify(filters.products)
        : filters.products;
    }
    if (filters.brands && filters.brands.length > 0) {
      params.brand = Array.isArray(filters.brands)
        ? JSON.stringify(filters.brands)
        : filters.brands;
    }
    if (filters.channels && filters.channels.length > 0) {
      params.channel = Array.isArray(filters.channels)
        ? JSON.stringify(filters.channels)
        : filters.channels;
    }
    if (filters.regions && filters.regions.length > 0) {
      params.region = Array.isArray(filters.regions)
        ? JSON.stringify(filters.regions)
        : filters.regions;
    }
    if (filters.clients && filters.clients.length > 0) {
      params.client = Array.isArray(filters.clients)
        ? JSON.stringify(filters.clients)
        : filters.clients;
    }
    if (
      filters.solver_status &&
      (Array.isArray(filters.solver_status)
        ? filters.solver_status.length > 0
        : filters.solver_status !== "")
    ) {
      params.solver_status = Array.isArray(filters.solver_status)
        ? JSON.stringify(filters.solver_status)
        : filters.solver_status;
    }

    const response = await apiClient.get(apiRoutes.cockpit.outputTable, {
      params: params,
      responseType: download ? "blob" : "json",
    });

    return response.data;
  } catch (error) {
    console.error("Failed to get output table data:", error);
    throw error;
  }
}

export async function getScenarioDetails(scenarioId) {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.scenarioDetails, {
      params: {
        scenario_id: scenarioId,
      },
    });
    console.log("Get Scenario Details - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get scenario details:", error);
    throw error;
  }
}

export async function updateScenario(scenarioId, updates) {
  try {
    const payload = {
      scenario_id: scenarioId,
      ...updates,
    };
    const response = await apiClient.patch(
      apiRoutes.cockpit.updateScenario,
      payload,
    );
    console.log("Update Scenario - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to update scenario:", error);
    throw error;
  }
}

export async function deleteScenario(scenarioId) {
  try {
    const response = await apiClient.delete(
      `${apiRoutes.cockpit.deleteScenario}?scenario_id=${scenarioId}`,
    );
    console.log("Delete Scenario - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to delete scenario:", error);
    throw error;
  }
}

export async function getAllMapData(
  scenarioId,
  filters = null,
  tooltipDetail = "channel",
  location = "location_option_2", // Frontend uses agnostic keys
  weightField = "volume_sellthrough", // Volume weighting parameter
) {
  try {
    // Map frontend location option to backend value
    const backendLocation = LOCATION_MAPPING[location] || location;

    const payload = {
      scenario_id: scenarioId,
      tooltip_detail: tooltipDetail,
      location: backendLocation,
      weight_field: weightField,
    };

    // Add filters to payload if provided
    if (filters) {
      payload.filters = filters;
    }

    const response = await apiClient.post(apiRoutes.cockpit.mapData, payload);
    console.log("Get All Map Data - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get all map data:", error);
    throw error;
  }
}

export async function getFilterOptions(
  scenarioId,
  filters = {},
  expandDimension = null,
  parameter = null,
) {
  try {
    const params = {
      scenario_id: scenarioId,
    };

    // Add filters if provided (for expand dimension feature)
    // Backend expects JSON-stringified arrays for GET params
    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value && Array.isArray(value) && value.length > 0) {
          // JSON stringify arrays so backend can parse them
          params[key] = JSON.stringify(value);
        } else if (value && !Array.isArray(value)) {
          // Single value - pass as is
          params[key] = value;
        }
      });
    }

    // Add expand_dimension if specified
    if (expandDimension) {
      params.expand_dimension = expandDimension;
    }

    // Add parameter if specified (for aggregation)
    if (parameter) {
      params.parameter = parameter;
    }

    const response = await apiClient.get(apiRoutes.cockpit.filterOptions, {
      params: params,
    });
    console.log("Get Filter Options - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get filter options:", error);
    throw error;
  }
}

export async function getPnlFilterOptions() {
  try {
    console.log("🎯 Getting P&L Filter Options (all scenarios from OUTPUT_11)");
    const response = await apiClient.get(apiRoutes.cockpit.pnlFilterOptions);
    console.log("✅ Get P&L Filter Options - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get P&L filter options:", error);
    throw error;
  }
}

export async function getFilteredOutputTableData(
  scenarioId,
  filters = {},
  fullTable = false,
  download = false,
) {
  try {
    const params = {
      scenario_id: scenarioId,
      full_table: fullTable,
      download: download,
    };

    // Add filter parameters if they exist
    if (filters.product) {
      params.product = filters.product;
    }
    if (filters.brand) {
      params.brand = filters.brand;
    }
    if (filters.channel) {
      params.channel = filters.channel;
    }
    if (filters.region) {
      params.region = filters.region;
    }
    if (filters.client) {
      params.client = filters.client;
    }

    const response = await apiClient.get(
      apiRoutes.cockpit.filteredOutputTable,
      {
        params: params,
        responseType: download ? "blob" : "json",
      },
    );
    console.log("Get Filtered Output Table Data - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get filtered output table data:", error);
    throw error;
  }
}

export async function getPnlData(scenarioId, filters = {}) {
  try {
    console.log(
      "🎯 Getting Comparative P&L Data for scenario:",
      scenarioId,
      "with filters:",
      filters,
    );

    // Build payload object with scenario_id and filters
    const payload = {
      scenario_id: scenarioId,
      ...filters, // Spread filters directly - arrays work naturally in POST
    };

    console.log("📤 API Request payload:", payload);

    const response = await apiClient.post(apiRoutes.cockpit.PnlData, payload);

    console.log(
      "✅ Get Comparative P&L Data - Response for scenario",
      scenarioId,
      ":",
      {
        plName: response.data?.plName,
        hasData: !!response.data?.plValues,
        valueKeys: response.data?.plValues
          ? Object.keys(response.data.plValues)
          : [],
        sampleValue: response.data?.plValues?.[1]?.volume || "no volume data",
      },
    );

    return response.data;
  } catch (error) {
    console.error("❌ Failed to get comparative P&L data:", error);
    throw error;
  }
}

export async function getMergeAnalysis(
  selectedVariants,
  calculationMethod = "method_costplus",
) {
  try {
    console.log("🔍 Getting Merge Analysis for variants:", selectedVariants);
    const response = await apiClient.post(apiRoutes.cockpit.mergeAnalysis, {
      selected_variants: selectedVariants,
      calculation_method: calculationMethod,
      // pnl_option_used is not required for merge analysis currently
    });
    console.log("✅ Get Merge Analysis - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get merge analysis:", error);
    throw error;
  }
}

export async function getComparisons() {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.comparisons);
    console.log("Get Comparisons - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get comparisons:", error);
    throw error;
  }
}

export async function createComparison(payload) {
  try {
    console.log("Creating comparison with payload:", payload);
    const response = await apiClient.post(
      apiRoutes.cockpit.comparisonCreate,
      payload,
    );
    console.log("Create Comparison - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to create comparison:", error);
    throw error;
  }
}

export async function updateComparison(comparisonId, updates) {
  try {
    console.log("Updating comparison:", comparisonId, updates);
    const response = await apiClient.patch(
      `${apiRoutes.cockpit.comparisonUpdate}${comparisonId}/`,
      updates,
    );
    console.log("Update Comparison - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to update comparison:", error);
    throw error;
  }
}

export async function deleteComparison(comparisonId) {
  try {
    console.log("Deleting comparison:", comparisonId);
    const response = await apiClient.delete(
      `${apiRoutes.cockpit.comparisonDelete}${comparisonId}/`,
    );
    console.log("Delete Comparison - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to delete comparison:", error);
    throw error;
  }
}

export async function getComparisonData(
  comparison,
  parentFilters = {},
  hierarchyOrder = [],
) {
  try {
    // Handle both comparison object and comparison_id
    const comparisonId =
      typeof comparison === "object" ? comparison.comparison_id : comparison;

    console.log("🔍 getComparisonData called with:", {
      comparison,
      comparisonId,
      parentFilters,
      hierarchyOrder,
    });

    const params = new URLSearchParams();

    // Add filter parameters - use PLURAL keys to match backend expectations
    // Backend expects: products, brands, channels, regions, clients (plural)
    if (parentFilters.regions && parentFilters.regions.length > 0) {
      parentFilters.regions.forEach((r) => params.append("regions", r));
    }
    if (parentFilters.clients && parentFilters.clients.length > 0) {
      parentFilters.clients.forEach((c) => params.append("clients", c));
    }
    if (parentFilters.products && parentFilters.products.length > 0) {
      parentFilters.products.forEach((p) => params.append("products", p));
    }
    if (parentFilters.channels && parentFilters.channels.length > 0) {
      parentFilters.channels.forEach((ch) => params.append("channels", ch));
    }
    if (parentFilters.brands && parentFilters.brands.length > 0) {
      parentFilters.brands.forEach((b) => params.append("brands", b));
    }

    // Add PNL flag filter if provided (special parameter, separate from standard filters)
    if (parentFilters.pnl_flag && parentFilters.pnl_flag !== "") {
      params.append("pnl_flag", parentFilters.pnl_flag);
    }

    // Add hierarchy parameter if provided
    if (hierarchyOrder && hierarchyOrder.length > 0) {
      hierarchyOrder.forEach((h) => params.append("hierarchy[]", h));
    }

    console.log("🌐 API request URL params:", params.toString());

    const response = await apiClient.get(
      `${apiRoutes.cockpit.comparisonData}${comparisonId}/?${params}`,
    );
    console.log("✅ Get Comparison Data - Response:", response.data);
    console.log("📊 Response data length:", response.data?.data?.length || 0);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get comparison data:", error);
    throw error;
  }
}

export async function getComparisonFilterOptions(comparisonId) {
  try {
    const response = await apiClient.get(
      `${apiRoutes.cockpit.comparisonFilterOptions}${comparisonId}/`,
    );
    console.log("Get Comparison Filter Options - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get comparison filter options:", error);
    throw error;
  }
}

export async function getPriceRouteHierarchicalData(
  comparisonId,
  parentFilters = {},
  hierarchyOrder = [],
) {
  try {
    console.log("🔍 getPriceRouteHierarchicalData called with:", {
      comparisonId,
      parentFilters,
      hierarchyOrder,
    });

    const params = new URLSearchParams({
      comparison_id: JSON.stringify(comparisonId),
      parent_filters: JSON.stringify(parentFilters),
    });

    // Add hierarchy parameter if provided
    if (hierarchyOrder && hierarchyOrder.length > 0) {
      params.append("hierarchy", hierarchyOrder.join(","));
    }

    console.log("🌐 API request URL params:", params.toString());

    const response = await apiClient.get(
      `${apiRoutes.cockpit.priceRouteData}?${params}`,
    );
    console.log(
      "✅ Get PriceRoute Hierarchical Data - Response:",
      response.data,
    );
    console.log("📊 Response data length:", response.data?.data?.length || 0);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get priceroute hierarchical data:", error);
    throw error;
  }
}

export async function getComparisonMetadata(comparisonId) {
  try {
    const response = await apiClient.get(
      `${apiRoutes.cockpit.comparisonMetadata}${comparisonId}/`,
    );
    console.log("Get Comparison Metadata - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get comparison metadata:", error);
    throw error;
  }
}

export async function getTopClientsData(scenarioId, filters = {}) {
  try {
    const payload = {
      scenario_id: scenarioId,
      ...filters, // Spread filters directly - arrays work naturally in POST
    };

    const response = await apiClient.post(
      apiRoutes.cockpit.topClients,
      payload,
    );
    console.log("Get Top Clients Data - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get top clients data:", error);
    throw error;
  }
}

export async function getChainVisualizationData(
  scenarioId,
  filters = {},
  weightField = null,
  consideredRoutes = "",
) {
  try {
    const payload = {
      scenario_id: scenarioId,
      ...filters, // Spread filters directly - arrays work naturally in POST
    };

    // Pass weight_field directly - backend expects: volume_sellthrough, volume_sellin, or "1" for not weighted
    if (weightField) {
      payload.weight_field = weightField;
    }

    // Pass considered_routes directly
    if (consideredRoutes) {
      payload.considered_routes = consideredRoutes;
    }

    const response = await apiClient.post(
      apiRoutes.cockpit.chainVisualization,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to get chain visualization data:", error);
    throw error;
  }
}

export async function downloadMissingValues(payload) {
  try {
    console.log("🔽 Downloading Missing Values for:", payload);
    const response = await apiClient.post(
      apiRoutes.cockpit.downloadMissingValues,
      payload,
      {
        responseType: "blob", // Important for file downloads
      },
    );
    console.log("✅ Download Missing Values - Response received");
    return response.data; // This will be a blob
  } catch (error) {
    console.error("❌ Failed to download missing values:", error);
    throw error;
  }
}

export async function startAsyncDownload(
  scenarioId,
  fullTable = false,
  filters = {},
) {
  try {
    const payload = {
      scenario_id: scenarioId,
      full_table: fullTable,
      filters: filters,
    };

    const response = await apiClient.post(
      apiRoutes.cockpit.outputTable,
      payload,
    );
    console.log("Start Async Download - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to start async download:", error);
    throw error;
  }
}

export async function getDownloadProgress(downloadId) {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.downloadProgress, {
      params: {
        download_id: downloadId,
      },
    });
    console.log("Get Download Progress - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get download progress:", error);
    throw error;
  }
}

export async function downloadFile(downloadId) {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.downloadProgress, {
      params: {
        download_id: downloadId,
        file: "true",
      },
      responseType: "blob",
    });
    console.log("Download File - Response received");
    return response.data;
  } catch (error) {
    console.error("Failed to download file:", error);
    throw error;
  }
}

export async function startAsyncPnlDownload(scenarioId, fullTable = false) {
  try {
    const payload = {
      scenario_id: scenarioId,
      full_table: fullTable,
    };
    const response = await apiClient.post(
      apiRoutes.cockpit.pnlDownload,
      payload,
    );
    console.log("Start Async PnL Download - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to start async PnL download:", error);
    throw error;
  }
}

export async function getScenarioPremises() {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.scenarioPremises);
    console.log("Get Scenario Premises - Response:", response.data);
    return response.data?.data;
  } catch (error) {
    console.error("Failed to get Scenario premises:", error);
    throw error;
  }
}

export async function updateScenarioPremise(premiseKey, data) {
  try {
    const response = await apiClient.patch(
      `${apiRoutes.cockpit.scenarioPremises}${premiseKey}/`,
      data,
    );
    console.log("Update Scenario Premise - Response:", response.data);
    return response.data?.data;
  } catch (error) {
    console.error("Failed to update Scenario premise:", error);
    throw error;
  }
}

export async function getPriceRouteData(
  scenarioId,
  filters = {},
  weightField = "volume_sellthrough",
) {
  try {
    console.log(
      "🎯 Getting Tax Route P&L Data for scenario:",
      scenarioId,
      "with filters:",
      filters,
      "weight field:",
      weightField,
    );

    const payload = {
      scenario_id: scenarioId,
      weight_field: weightField, // Pass directly: "volume_sellthrough", "volume_sellin", or "1"
      ...filters, // Spread filters directly - arrays work naturally in POST
    };

    console.log("📤 API Request payload:", payload);

    const response = await apiClient.post(
      apiRoutes.cockpit.priceRouteData,
      payload,
    );

    console.log(
      "✅ Get Tax Route P&L Data - Response for scenario",
      scenarioId,
      ":",
      response.data,
    );

    return response.data;
  } catch (error) {
    console.error("❌ Failed to get Tax Route P&L data:", error);
    throw error;
  }
}

export async function startAsyncPriceRoutePnlDownload(
  scenarioId,
  fullTable = false,
) {
  try {
    const payload = {
      scenario_id: scenarioId,
      full_table: fullTable,
    };
    const response = await apiClient.post(
      apiRoutes.cockpit.priceRoutePnlDownload,
      payload,
    );
    console.log(
      "Start Async Tax Route PnL Download - Response:",
      response.data,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to start async Tax Route PnL download:", error);
    throw error;
  }
}

export async function getPriceRouteFilterOptions(scenarioIds = []) {
  try {
    console.log(
      "🎯 Getting Tax Route P&L Filter Options for scenarios:",
      scenarioIds,
    );

    const params = {};

    if (scenarioIds && scenarioIds.length > 0) {
      params.scenario_ids = scenarioIds.join(",");
    }

    const response = await apiClient.get(
      apiRoutes.cockpit.priceRouteFilterOptions,
      {
        params: params,
      },
    );
    console.log(
      "✅ Get Tax Route P&L Filter Options - Response:",
      response.data,
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get Tax Route P&L filter options:", error);
    throw error;
  }
}

export async function getMaskConfig() {
  try {
    console.log("🎯 Getting Mask Configuration");
    const response = await apiClient.get(apiRoutes.cockpit.maskConfig);
    console.log("✅ Get Mask Config - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get mask config:", error);
    throw error;
  }
}

// ###### FOLDER MANAGEMENT API CALLS

export async function getFolders() {
  try {
    const response = await apiClient.get(apiRoutes.cockpit.folders);
    console.log("Get Folders - Response:", response.data);
    return response.data.folders;
  } catch (error) {
    console.error("Failed to get folders:", error);
    throw error;
  }
}

export async function createFolder(name, parentFolderId = null, color = null) {
  try {
    const payload = {
      name,
      parent_folder_id: parentFolderId,
      color,
    };
    const response = await apiClient.post(
      apiRoutes.cockpit.foldersCreate,
      payload,
    );
    console.log("Create Folder - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to create folder:", error);
    throw error;
  }
}

export async function updateFolder(folderId, updates) {
  try {
    const response = await apiClient.patch(
      `${apiRoutes.cockpit.folders}${folderId}/`,
      updates,
    );
    console.log("Update Folder - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to update folder:", error);
    throw error;
  }
}

export async function getFolderDeletePreview(folderId) {
  try {
    const response = await apiClient.get(
      `${apiRoutes.cockpit.folders}${folderId}/delete-preview/`,
    );
    console.log("Get Folder Delete Preview - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to get folder delete preview:", error);
    throw error;
  }
}

export async function deleteFolder(folderId) {
  try {
    const response = await apiClient.delete(
      `${apiRoutes.cockpit.folders}${folderId}/delete/`,
      { data: { confirm: true } },
    );
    console.log("Delete Folder - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to delete folder:", error);
    throw error;
  }
}

export async function archiveScenarios(scenarioIds, archive = true) {
  try {
    const response = await apiClient.post(apiRoutes.cockpit.scenariosArchive, {
      scenario_ids: scenarioIds,
      archive,
    });
    console.log("Archive Scenarios - Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to archive scenarios:", error);
    throw error;
  }
}
